import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, CheckCircle2, FileSpreadsheet, Upload, X } from 'lucide-react';
import { api, ApiError } from '../hooks/api.js';
import {
  CSV_REJECT_LABELS,
  SUBSCRIBER_CSV_TEMPLATE,
  csvColumnCount,
  parseSubscriberCsv,
  type CsvColumnMapping,
  type EmailList,
  type ImportSubscribersInput,
  type ImportSubscribersResult,
  type VariableDefinitions,
  type SubscriberCsvField,
} from '@blogforge/shared';

// ─────────────────────────────────────────────────────────────────
// CSV import for one audience list.
//
// The file is read in the browser and parsed with the SAME shared parser
// the server runs, so the preview the user approves (columns, counts, the
// rows that will be skipped and why) is exactly what the import does. The
// raw text is posted; the server re-parses and is the authority.
//
// Flow: pick a file → confirm the detected columns → import → per-row summary.
// ─────────────────────────────────────────────────────────────────

type Stage = 'pick' | 'preview' | 'result';

interface Props {
  list: EmailList;
  /** Plan cap for one list, or null when unlimited. */
  maxPerList: number | null;
  onClose: () => void;
  /** Called with the server's summary so the page can refresh and report it. */
  onImported: (result: ImportSubscribersResult) => void;
}

/** Every reason the server can report, in user-facing words. */
const REASON_LABELS: Record<string, string> = {
  ...CSV_REJECT_LABELS,
  already_in_list: 'Already in this list',
  limit_reached: 'Plan subscriber cap reached',
};

const FIELD_LABELS: Record<SubscriberCsvField, string> = {
  subscriberId: 'Subscriber ID',
  email: 'Email',
};

const FIELD_HINTS: Record<SubscriberCsvField, string> = {
  subscriberId: 'Optional',
  email: 'Required',
};

export function ImportSubscribersModal({ list, maxPerList, onClose, onImported }: Props) {
  const [stage, setStage] = useState<Stage>('pick');
  const [fileName, setFileName] = useState<string | null>(null);
  const [csvText, setCsvText] = useState('');
  const [mapping, setMapping] = useState<CsvColumnMapping | null>(null);
  const [hasHeader, setHasHeader] = useState<boolean | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportSubscribersResult | null>(null);

  // Parsing is pure and fast, so it runs on every render where the input
  // changed rather than being mirrored into state.
  const parsed = useMemo(() => {
    if (!csvText) return null;
    const options: {
      mapping?: Partial<Record<SubscriberCsvField, number | null | undefined>>;
      hasHeader?: boolean;
      definitions: VariableDefinitions;
    } = { definitions: list.attributes };
    if (mapping) options.mapping = mapping;
    if (hasHeader !== null) options.hasHeader = hasHeader;
    return parseSubscriberCsv(csvText, options);
  }, [csvText, mapping, hasHeader, list.attributes]);

  const columns = useMemo(() => (csvText ? csvColumnCount(csvText) : 0), [csvText]);

  /** Header cells when the file has one, else generic column names. */
  const columnOptions = useMemo(() => {
    const header = parsed?.ok ? parsed.header : null;
    return Array.from({ length: columns }, (_, index) => ({
      index,
      label: header?.[index]?.trim()
        ? `${header[index]!.trim()} (column ${index + 1})`
        : `Column ${index + 1}`,
    }));
  }, [columns, parsed]);

  const remaining = maxPerList === null ? null : Math.max(0, maxPerList - list.subscriberCount);
  const readyCount = parsed?.ok ? parsed.valid.length : 0;
  const willOverflow = remaining !== null && readyCount > remaining;

  const importMutation = useMutation({
    mutationFn: async () => {
      const payload: ImportSubscribersInput = { csv: csvText };
      if (fileName) payload.fileName = fileName;
      if (parsed?.ok) {
        payload.hasHeader = parsed.hasHeader;
        payload.mapping = {
          subscriberId: parsed.mapping.subscriberId,
          email: parsed.mapping.email,
        };
        // Send the attribute columns the preview resolved, so a definitions
        // change between preview and import cannot shift them.
        if (Object.keys(parsed.attributeMapping).length > 0) {
          payload.attributeMapping = parsed.attributeMapping;
        }
      }
      return api.emailLists.import(list.id, payload);
    },
    onSuccess: (data) => {
      setResult(data);
      setStage('result');
      onImported(data);
    },
  });

  const handleFile = (file: File) => {
    setReadError(null);
    if (file.size > 8 * 1024 * 1024) {
      setReadError('That file is larger than 8 MB — split it and import in batches.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      if (!text.trim()) {
        setReadError('That file is empty.');
        return;
      }
      setFileName(file.name);
      setCsvText(text);
      // A fresh file restarts detection: no stale mapping or header choice.
      setMapping(null);
      setHasHeader(null);
      setStage('preview');
    };
    reader.onerror = () => setReadError('Could not read that file.');
    reader.readAsText(file);
  };

  const error =
    readError ??
    (importMutation.error instanceof ApiError
      ? importMutation.error.message
      : importMutation.error instanceof Error
        ? importMutation.error.message
        : null);

  const setColumn = (field: SubscriberCsvField, value: string) => {
    const next: CsvColumnMapping = {
      subscriberId: parsed?.ok ? parsed.mapping.subscriberId : null,
      email: parsed?.ok ? parsed.mapping.email : null,
      ...(mapping ?? {}),
    };
    next[field] = value === '' ? null : Number(value);
    setMapping(next);
  };

  const busy = importMutation.isPending;

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="modal-card w-full max-w-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Import subscribers into ${list.name}`}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-ink text-[17px] font-bold tracking-tight">
              {stage === 'result' ? 'Import finished' : 'Import subscribers'}
            </h2>
            <p className="text-muted mt-0.5 text-[12.5px]">
              {stage === 'result'
                ? list.name
                : `Into “${list.name}” · CSV with email, and optionally subscriber ID and name`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-muted hover:bg-card-2 hover:text-ink-soft rounded p-1 disabled:opacity-40"
            aria-label="Close"
          >
            <X size={17} />
          </button>
        </div>

        {/* ── Stage 1: pick a file ─────────────────────────────── */}
        {stage === 'pick' && (
          <div className="space-y-4">
            <label
              className="border-line-strong bg-bg/40 hover:border-accent-border hover:bg-accent/5 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-10 text-center transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (file) handleFile(file);
              }}
            >
              <FileSpreadsheet size={26} className="text-accent" strokeWidth={1.7} />
              <span className="text-ink text-sm font-semibold">
                Drop a CSV here, or click to choose a file
              </span>
              <span className="text-muted text-[12px]">
                Up to 8 MB / 20,000 rows per import · duplicates are skipped, not merged
              </span>
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                  e.target.value = '';
                }}
              />
            </label>

            <details className="text-ink-soft text-[12.5px]">
              <summary className="hover:text-ink cursor-pointer">Expected columns</summary>
              <p className="text-muted mt-2">
                A header row is matched automatically; without one the columns are guessed from the
                data (you can correct them next). Order does not matter.
              </p>
              <pre className="bg-bg-soft mt-2 overflow-x-auto rounded-lg p-3 text-[11.5px]">
                {SUBSCRIBER_CSV_TEMPLATE.example}
              </pre>
            </details>

            {error && (
              <p className="border-danger/20 bg-danger/8 text-danger rounded-lg border px-3 py-2 text-[12.5px]">
                {error}
              </p>
            )}
          </div>
        )}

        {/* ── Stage 2: confirm columns + preview ───────────────── */}
        {stage === 'preview' && (
          <div className="space-y-4">
            <div className="border-line bg-bg/50 flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
              <span className="text-ink-soft truncate text-[12.5px] font-medium">
                {fileName ?? 'Pasted CSV'} · {columns} column{columns === 1 ? '' : 's'}
                {parsed?.ok
                  ? ` · ${parsed.delimiter === '\t' ? 'tab' : parsed.delimiter === ';' ? 'semicolon' : 'comma'} separated`
                  : ''}
              </span>
              <button
                type="button"
                onClick={() => {
                  setStage('pick');
                  setCsvText('');
                  setMapping(null);
                  setHasHeader(null);
                }}
                className="btn-ghost shrink-0 px-2 py-1 text-[11.5px]"
              >
                Choose another
              </button>
            </div>

            {/* Column mapping */}
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="field-label mb-0">Columns</p>
                <label className="text-ink-soft flex cursor-pointer items-center gap-1.5 text-[11.5px]">
                  <input
                    type="checkbox"
                    className="border-line-strong rounded"
                    checked={parsed?.ok ? parsed.hasHeader : false}
                    onChange={(e) => setHasHeader(e.target.checked)}
                  />
                  First row is a header
                </label>
              </div>

              <div className="grid gap-2 sm:grid-cols-3">
                {(['subscriberId', 'email'] as const).map((field) => (
                  <div key={field}>
                    <label className="field-label mb-1 block" htmlFor={`csv-${field}`}>
                      {FIELD_LABELS[field]}
                      <span className="text-muted ml-1.5 font-normal normal-case">
                        {FIELD_HINTS[field]}
                      </span>
                    </label>
                    <select
                      id={`csv-${field}`}
                      className="field-input w-full"
                      value={
                        parsed?.ok && parsed.mapping[field] !== null
                          ? String(parsed.mapping[field])
                          : ''
                      }
                      onChange={(e) => setColumn(field, e.target.value)}
                    >
                      <option value="">— none —</option>
                      {columnOptions.map((column) => (
                        <option key={column.index} value={column.index}>
                          {column.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {parsed?.ok && Object.keys(parsed.attributeMapping).length > 0 && (
                <p className="text-muted mt-2 flex flex-wrap items-center gap-1.5 text-[11.5px]">
                  <span className="text-ink-soft font-medium">Attribute columns:</span>
                  {Object.entries(parsed.attributeMapping).map(([attribute, column]) => (
                    <span
                      key={attribute}
                      className="bg-ink/5 text-ink-soft rounded-full px-2 py-0.5 font-mono text-[11px]"
                      title={`Column ${column + 1} → ${attribute}`}
                    >
                      {columnOptions[column]?.label.replace(/ \(column \d+\)$/, '') ??
                        `Column ${column + 1}`}{' '}
                      → {attribute}
                    </span>
                  ))}
                </p>
              )}

              {parsed?.ok && Object.keys(list.attributes).length > 0 && (
                <p className="text-muted mt-1.5 text-[11.5px]">
                  Columns whose header matches an attribute name are imported into it; the list’s
                  other attributes are left unset (unless one is required, which is reported per
                  row).
                </p>
              )}

              {parsed?.ok && parsed.mappingSource !== 'header' && (
                <p className="text-muted mt-2 flex items-start gap-1.5 text-[11.5px]">
                  <AlertTriangle size={12} className="text-accent mt-0.5 shrink-0" />
                  {parsed.mappingSource === 'sniffed'
                    ? 'No usable header found, so these columns were guessed from the data — check them before importing.'
                    : 'Using the columns you picked.'}
                </p>
              )}
            </div>

            {/* Counts */}
            {parsed && !parsed.ok && (
              <p className="border-danger/20 bg-danger/8 text-danger rounded-lg border px-3 py-2 text-[12.5px]">
                {parsed.error}
              </p>
            )}

            {parsed?.ok && (
              <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Stat label="Rows found" value={parsed.totalRows} />
                  <Stat label="Ready" value={parsed.valid.length} tone="ok" />
                  <Stat
                    label="Duplicates"
                    value={
                      parsed.rejectedCounts.duplicate_email +
                      parsed.rejectedCounts.duplicate_subscriber_id
                    }
                  />
                  <Stat
                    label="Invalid"
                    value={
                      parsed.rejectedCounts.invalid_email +
                      parsed.rejectedCounts.missing_email +
                      parsed.rejectedCounts.too_long
                    }
                    tone={
                      parsed.rejectedCounts.invalid_email +
                        parsed.rejectedCounts.missing_email +
                        parsed.rejectedCounts.too_long >
                      0
                        ? 'warn'
                        : undefined
                    }
                  />
                </div>

                {willOverflow && (
                  <p className="border-line bg-bg/60 text-ink-soft flex items-start gap-2 rounded-lg border px-3 py-2 text-[12px]">
                    <AlertTriangle size={13} className="text-accent mt-0.5 shrink-0" />
                    <span>
                      This list has room for {remaining} more subscriber{remaining === 1 ? '' : 's'}{' '}
                      on the{' '}
                      {maxPerList === null ? 'current' : `${list.subscriberCount}/${maxPerList}`}{' '}
                      plan limit. The first {remaining} ready row{remaining === 1 ? '' : 's'} will
                      be imported; the rest are reported and left out.
                    </span>
                  </p>
                )}

                {parsed.valid.length > 0 && (
                  <div className="border-line overflow-hidden rounded-lg border">
                    <table className="w-full text-left text-[12.5px]">
                      <thead className="bg-bg-soft text-muted">
                        <tr>
                          <th className="px-3 py-2 font-semibold">{FIELD_LABELS.subscriberId}</th>
                          <th className="px-3 py-2 font-semibold">{FIELD_LABELS.email}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.valid.slice(0, 5).map((row) => (
                          <tr key={row.row} className="border-line border-t">
                            <td className="text-muted px-3 py-2">{row.subscriberId ?? '—'}</td>
                            <td className="text-ink px-3 py-2">{row.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsed.valid.length > 5 && (
                      <p className="border-line bg-bg-soft text-muted border-t px-3 py-1.5 text-[11.5px]">
                        …and {parsed.valid.length - 5} more
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {error && (
              <p className="border-danger/20 bg-danger/8 text-danger rounded-lg border px-3 py-2 text-[12.5px]">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={() => setStage('pick')}
                disabled={busy}
                className="btn-ghost px-3 py-2 text-[13px]"
              >
                <ArrowLeft size={14} />
                Back
              </button>
              <button
                type="button"
                onClick={() => importMutation.mutate()}
                disabled={busy || !parsed?.ok || parsed.valid.length === 0}
                className="btn-primary disabled:opacity-50"
              >
                <Upload size={14} />
                {busy
                  ? 'Importing…'
                  : `Import ${willOverflow ? Math.max(0, remaining ?? 0) : readyCount} subscriber${
                      readyCount === 1 ? '' : 's'
                    }`}
              </button>
            </div>
          </div>
        )}

        {/* ── Stage 3: per-row summary ─────────────────────────── */}
        {stage === 'result' && result && (
          <div className="space-y-4">
            <div className="border-line bg-bg/50 flex items-start gap-3 rounded-lg border px-3 py-3">
              <CheckCircle2 size={17} className="text-ok mt-0.5 shrink-0" />
              <p className="text-ink-soft text-[13px]">
                <span className="text-ink font-semibold">
                  {result.imported} subscriber{result.imported === 1 ? '' : 's'} added.
                </span>{' '}
                “{list.name}” now holds {result.list.subscriberCount}
                {maxPerList === null ? '' : `/${maxPerList}`}.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="Rows found" value={result.totalRows} />
              <Stat label="Imported" value={result.imported} tone="ok" />
              <Stat label="Skipped" value={result.duplicates} />
              <Stat
                label="Not imported"
                value={result.invalid + result.overLimit}
                tone={result.invalid + result.overLimit > 0 ? 'warn' : undefined}
              />
            </div>

            {result.errors.length > 0 && (
              <div className="border-line overflow-hidden rounded-lg border">
                <div className="max-h-56 overflow-y-auto">
                  <table className="w-full text-left text-[12.5px]">
                    <thead className="bg-bg-soft text-muted sticky top-0">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Row</th>
                        <th className="px-3 py-2 font-semibold">Email</th>
                        <th className="px-3 py-2 font-semibold">Why</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((row) => (
                        <tr key={`${row.row}-${row.reason}`} className="border-line border-t">
                          <td className="text-muted px-3 py-1.5">{row.row}</td>
                          <td className="text-ink px-3 py-1.5">{row.email ?? '—'}</td>
                          <td className="text-muted px-3 py-1.5">
                            {REASON_LABELS[row.reason] ?? row.reason}
                            {row.message ? (
                              <span className="text-muted/80 block text-[11px]">{row.message}</span>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {result.errorsTruncated && (
                  <p className="border-line bg-bg-soft text-muted border-t px-3 py-1.5 text-[11.5px]">
                    Only the first {result.errors.length} affected rows are listed.
                  </p>
                )}
              </div>
            )}

            {result.duplicates === 0 && result.invalid === 0 && result.overLimit === 0 && (
              <p className="text-muted text-[12.5px]">
                Every row in the file was added — nothing had to be skipped.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setStage('pick');
                  setCsvText('');
                  setResult(null);
                  setMapping(null);
                  setHasHeader(null);
                  setFileName(null);
                }}
                className="btn-secondary rounded-lg px-4 py-2 text-sm"
              >
                Import another file
              </button>
              <button type="button" onClick={onClose} className="btn-primary">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Small labelled number used in the preview and the summary. */
function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'ok' | 'warn' | undefined;
}) {
  const color = tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-danger' : 'text-ink';
  return (
    <div className="border-line bg-bg/40 rounded-lg border px-3 py-2">
      <p className="text-muted text-[11px] font-semibold tracking-wide uppercase">{label}</p>
      <p className={`mt-0.5 text-[17px] font-bold tabular-nums ${color}`}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}
